import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import CommonReactTable from "../CommonUtils/CommonReactTable";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { config } from "../CommonUtils/CommonURLS";
import { failureAlert, successAlert2 } from "../CommonUtils/SweetAlerts";
import StudentsFormVerification from "./StudentFormVerification";
import StudentFormReceiving from "./StudentFormReceiving";
import StudentPreview from "../Student Services/StuServices/StudentPreview";
import { digisignFunction } from "../CommonUtils/DIGISIGN/digisignFunction";
import { initiateESign } from "../CommonUtils/CommonValidations";
import CommonCardHeader from "../CommonUtils/CommonCardHeader";

export default function SuperAdminDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showCheckModal, setShowCheckModal] = useState(false);
    const [showCollegeVerificationModal, setShowCollegeVerificationModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [verificationDetails, setVerificationDetails] = useState([]);
    const [verifiedStatus, setVerifiedStatus] = useState("");

    const [cteAdminDecisions, setCteAdminDecisions] = useState({});
    const [cteAdminPostingStudentId, setCteAdminPostingStudentId] = useState("");
    const [cteAdminRemarks, setCteAdminRemarks] = useState({});
    const [selectedStuIds, setSelectedStuIds] = useState([]);
    const [digiSignSubmitting, setDigiSignSubmitting] = useState(false);
    const [pdfBase64, setPdfBase64] = useState("");
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [selectedDigii, setSelectedDigii] = useState(null);

    const workflowTitle = "Students Digi Sign";
    const workflowSubtitle = "Review Applications";
    const shouldShowMainSubmitButton = verificationDetails?.length === 0;

    const handleCteDecisionChange = (student, decision) => {
        const studentKey = student?.student_reg_no;
        setCteAdminDecisions((prev) => ({ ...prev, [studentKey]: decision }));
    };

    const handleCteDecisionSubmit = async (student) => {
        const studentKey = student?.student_reg_no;
        const decision = cteAdminDecisions[studentKey];

        if (!decision) {
            failureAlert("Please select Approve or Reject.");
            return;
        }
        if (decision === "Rejected" && !cteAdminRemarks[studentKey]) {
            failureAlert("Please provide remarks for rejection.");
            return;
        }

        const payload = {
            stuId: student?.student_reg_no,
            ctAdminStatus: decision,
            remarks: decision === "Rejected" ? cteAdminRemarks[studentKey] || "" : "",
        };

        setCteAdminPostingStudentId(studentKey);
        try {
            const res = await CommonAxiosPost(`${config.url.STU_URL}insertctadminstatus`, payload);
            if (res?.data?.status === true || res?.data?.scode === "01") {
                successAlert2(res?.data?.message || res?.data?.sdesc || `${decision} submitted successfully`);
                setCteAdminDecisions((prev) => ({ ...prev, [studentKey]: "" }));
                setCteAdminRemarks((prev) => ({ ...prev, [studentKey]: "" }));
                fetchStudents();
            } else {
                failureAlert(res?.data?.message || res?.data?.sdesc || "Submission failed");
            }
        } catch (error) {
            console.error("Error submitting decision:", error);
            failureAlert("Something went wrong while submitting decision.");
        } finally {
            setCteAdminPostingStudentId("");
        }
    };

    const getStatusBadge = (status) => {
        const value = String(status || "Pending").trim();
        if (value === "Approved") return <span className="badge bg-success">Approved</span>;
        if (value === "Rejected") return <span className="badge bg-danger">Rejected</span>;
        if (value === "NOC Issued") return <span className="badge bg-primary">NOC Issued</span>;
        if (value === "Pending Review") return <span className="badge bg-info">Pending Review</span>;
        return <span className="badge bg-warning text-dark">{status}</span>;
    };

    const allRowIds = students.map((item) => item?.student_reg_no).filter(Boolean);
    const isAllSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedStuIds.includes(id));

    const handleCheckAllChange = (e) => {
        if (e.target.checked) {
            setSelectedStuIds(allRowIds);
            return;
        }
        setSelectedStuIds([]);
    };

    const handleRowCheckChange = (studentId, checked) => {
        if (!studentId) return;
        setSelectedStuIds((prev) => {
            if (checked) {
                if (prev.includes(studentId)) return prev;
                return [...prev, studentId];
            }
            return prev.filter((id) => id !== studentId);
        });
    };

    const handleDigiSignSubmit = () => {
        if (!CheckboxValidation()) return;
        const stuIdString = selectedStuIds.join(",");
        if (!stuIdString) {
            failureAlert("Student ID not found.");
            return;
        }
        setDigiSignSubmitting(true);
        // Fetch proceedings PDF from API
        // CommonAxiosGet(`${config.url.STU_URL}generatestudentproceedings`)
        CommonAxiosGet(`${config.url.STU_URL}generatestudentproceedings?stuids=${stuIdString}`)
            .then((res) => {
                if (res?.data?.scode === "01" && res?.data?.pdf) {
                    setPdfBase64(res?.data?.pdf);
                    setSelectedDigii(stuIdString);
                    setShowPdfModal(true);
                } else {
                    failureAlert("PDF not available.");
                }
            })
            .catch((err) => {
                console.error("Error fetching PDF:", err);
                failureAlert("Failed to load PDF.");
            })
            .finally(() => {
                setDigiSignSubmitting(false);
            });
    };

    const dskConfirmationFunc = () => {
        if (!pdfBase64) {
            failureAlert("No PDF available for signing.");
            return;
        }
        try {
            if (!selectedDigii) {
                failureAlert("Student ID not found.");
                return;
            }
            let postData = {
                path: `${config.url.STU_URL}updatesuperadminstatus`,
                params: {
                    stuIds: selectedStuIds,
                    pdfSign: pdfBase64,
                },
            };

            digisignFunction(pdfBase64, "-1", postData, "", "SuperAdminDetails");
            setShowPdfModal(false);
            setPdfBase64("");
            setSelectedDigii(null);

            setTimeout(() => {
                setSelectedStuIds([]);
                fetchStudents();
            }, 1500);
        } catch (error) {
            console.error("Error:", error);
            failureAlert("Digi sign failed.");
        }
    };

    const columns = [
        {
            id: "slNo",
            header: (
                <div className="d-flex align-items-center justify-content-center gap-1">
                    <Form.Check
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleCheckAllChange}
                        aria-label="Check All"
                    />
                    <span>S.No</span>
                </div>
            ),
            cell: ({ row }) => {
                const rowStudentId = row.original?.student_reg_no;
                const checked = selectedStuIds.includes(rowStudentId);
                return (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <Form.Check
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => handleRowCheckChange(rowStudentId, e.target.checked)}
                            aria-label={`Select ${rowStudentId || "student"}`}
                        />
                        <span>{row.index + 1}</span>
                    </div>
                );
            },
        },
        {
            id: "student_reg_no",
            header: "Student ID",
            accessorKey: "student_reg_no",
            meta: { style: { textAlign: "center" } },
        },
        {
            id: "student_name",
            header: "Student Name",
            accessorKey: "student_name",
            cell: ({ row }) => <div style={{ textAlign: "left" }}>{row.original.student_name}</div>,
        },
        {
            id: "course_name",
            header: "Course/Year/SEM",
            accessorKey: "course_name",
            cell: ({ row }) => <div style={{ textAlign: "left" }}>{row.original.course_name}</div>,
        },
        {
            id: "prsent_collname1",
            header: "From College Name",
            accessorKey: "prsent_collname",
        },
        {
            id: "trans_collname",
            header: "Transfer College Name",
            accessorKey: "trans_collname",
        },
        {
            id: "transfer_reason",
            header: "Reason for Transfer",
            accessorKey: "transfer_reason",
            cell: ({ row }) => <div style={{ textAlign: "left" }}>{row.original.transfer_reason}</div>,
        },
        {
            id: "student_particulars",
            header: "Student Preview",
            cell: ({ row }) => (
                <Button variant="outline-secondary" size="sm" onClick={() => handlePreviewClick(row.original)}>
                    <FaEye />
                </Button>
            ),
            meta: { style: { textAlign: "center" } },
        },
        {
            id: "college_verification",
            header: "Verified by Current College(From College)",
            cell: ({ row }) => (
                <Button variant="outline-primary" size="sm" onClick={() => handleCollegeVerificationClick(row.original, "verification")}>
                    <FaEye />
                </Button>
            ),
            meta: { style: { textAlign: "center" } },
        },
        {
            id: "college_receiving",
            header: "Received by New College(To College)",
            cell: ({ row }) => (
                <Button variant="outline-primary" size="sm" onClick={() => handleCollegeVerificationClick(row.original, "Receiving")}>
                    <FaEye />
                </Button>
            ),
            meta: { style: { textAlign: "center" } },
        },
        {
            id: "ct_admin_status",
            header: "CT Admin Status",
            cell: ({ row }) => getStatusBadge(row.original.ct_admin_status),
            meta: { style: { textAlign: "center" } },
        },
        {
            id: "Final status",
            header: "Final Status",
            cell: ({ row }) => getStatusBadge("pending"),
            meta: { style: { textAlign: "center" } },
        },
    ];

    useEffect(() => {
        fetchStudents();
    }, []);

    useEffect(() => {
        const status = location?.state?.eSignStatus;
        if (!status) return;

        if (status === "S") {
            Swal.fire({
                title: "E-signed Successfully!",
                icon: "success",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK",
                backdrop: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
        } else if (status === "F") {
            Swal.fire({
                title: "E-sign Failed!",
                text: "Please try again.",
                icon: "error",
                confirmButtonColor: "#d33",
                confirmButtonText: "OK",
                backdrop: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
        } else {
            Swal.fire({
                title: "Invalid Response",
                text: "Something went wrong.",
                icon: "warning",
                confirmButtonText: "OK",
                backdrop: false,
            });
        }

        navigate(location.pathname, { replace: true, state: null });
    }, [location, navigate]);

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const endpoint = "getsuperadmindeptcollegedetails";
            const response = await CommonAxiosGet(`${config.url.STU_URL}${endpoint}`);
            const rows = response?.data?.collegedetails || response?.data?.tocollegedetails || response?.data?.data || [];

            if (response?.data?.scode === "01" && Array.isArray(rows) && rows.length > 0) {
                setStudents(rows);
                setSelectedStuIds((prev) => prev.filter((id) => rows.some((item) => item?.student_reg_no === id)));
            } else {
                setStudents([]);
                setSelectedStuIds([]);
            }
        } catch (error) {
            console.error("Error fetching students:", error);
            setStudents([]);
            setSelectedStuIds([]);
            failureAlert("Failed to fetch student data.");
        } finally {
            setLoading(false);
        }
    };

    const handlefetchDetails = (studentId) => {
        if (!studentId) {
            setVerificationDetails([]);
            return;
        }
        const endpoint = `getStudentVerificationData?StudentId=${studentId}`;
        CommonAxiosGet(`${config.url.STU_URL}${endpoint}`)
            .then((res) => {
                if (res?.data?.status === true || res?.data?.scode === "01") {
                    setVerificationDetails(res?.data?.data);
                } else {
                    setVerificationDetails([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching verification details:", error);
                setVerificationDetails([]);
            });
    };

    const handlePreviewClick = (student) => {
        setSelectedStudent(student);
        setShowPreviewModal(true);
    };

    const handleCheckClick = (student) => {
        setSelectedStudent(student);
        setVerificationDetails([]);
        setShowCheckModal(true);
        handlefetchDetails(student?.student_reg_no);
    };

    const handleCollegeVerificationClick = (student, type) => {
        setSelectedStudent(student);
        setVerificationDetails([]);
        setShowCollegeVerificationModal(true);
        setVerifiedStatus(type);
        handlefetchDetails(student?.student_reg_no);
    };

    if (loading && students.length === 0) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading students...</p>
            </div>
        );
    }

    const CheckboxValidation = () => {
        if (selectedStuIds.length === 0) {
            failureAlert("Please select at least one student.");
            return false;
        }
        return true;
    }

    return (
        <Container fluid className="py-4">
            <section className="workflow-section-surface">
                <CommonCardHeader title={workflowTitle} subtitle={workflowSubtitle} />
                <div className="workflow-section-body">
                    {students.length > 0 ? (<>
                        <CommonReactTable
                            data={students}
                            columns={columns}
                            filename="StudentsDigiSignApplications"
                            headerName="Students Digi Sign Applications"
                            showFooter={false}
                            showPagination={false}
                            showExcel={true}
                            showPrint={true}
                            excelFileName="StudentsDigiSignApplications"
                        />
                        <div className="d-flex justify-content-end mt-3 gap-2">
                            <Button variant="success" onClick={handleDigiSignSubmit} >
                                Click here to Sign
                            </Button>

                        </div>
                    </>) : (
                        <div style={{ textAlign: "center", color: "red", fontWeight: "bold" }} > ** No data Found ** </div>
                    )}
                </div>
            </section>

            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Student Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <StudentPreview studentRegNo={selectedStudent?.student_reg_no} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCheckModal} onHide={() => setShowCheckModal(false)} size="xl" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Review Application - {selectedStudent?.student_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Card className="mb-3 bg-light">
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <strong>Student Id:</strong> {selectedStudent?.student_reg_no || "-"}
                                </Col>
                                <Col md={4}>
                                    <strong>Student Name:</strong>
                                    {selectedStudent?.student_name || "-"}
                                </Col>
                                <Col md={5}>
                                    <strong>To College:</strong> {selectedStudent?.trans_collname || "-"}
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col md={3}>
                                    <strong>Course:</strong> {selectedStudent?.course_name || "-"}
                                </Col>
                                <Col md={4}>
                                    <strong>Reason for Transfer:</strong> {selectedStudent?.transfer_reason || "-"}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <StudentsFormVerification
                        selectedStudent={selectedStudent}
                        setShowCheckModal={setShowCheckModal}
                        fetchStudents={fetchStudents}
                        setSubmitting={setSubmitting}
                        verificationDetails={verificationDetails}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="secondary" onClick={() => setShowCheckModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCollegeVerificationModal} onHide={() => setShowCollegeVerificationModal(false)} size="xl" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>College Verification - {selectedStudent?.student_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Card className="mb-3 bg-light">
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <strong>Student Id:</strong> {selectedStudent?.student_reg_no || "-"}
                                </Col>
                                <Col md={4}>
                                    <strong>Student Name:</strong>
                                    {selectedStudent?.student_name || "-"}
                                </Col>
                                <Col md={5}>
                                    <strong>To College:</strong> {selectedStudent?.trans_collname || "-"}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {verifiedStatus === "verification" && (
                        <StudentsFormVerification
                            selectedStudent={selectedStudent}
                            setShowCheckModal={setShowCollegeVerificationModal}
                            fetchStudents={fetchStudents}
                            setSubmitting={setSubmitting}
                            verificationDetails={verificationDetails}
                        />
                    )}

                    {verifiedStatus === "Receiving" && (
                        <StudentFormReceiving
                            selectedStudent={selectedStudent}
                            setShowCheckModal={setShowCollegeVerificationModal}
                            fetchStudents={fetchStudents}
                            setSubmitting={setSubmitting}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="secondary" onClick={() => setShowCollegeVerificationModal(false)}>
                        Cancel
                    </Button>
                    {verificationDetails?.length > 0 ? null : (
                        <Button type="submit" form="student-verification-form" variant="primary">
                            Submit
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
            <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Proceedings PDF Preview</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ height: "80vh", padding: 0 }}>
                    {pdfBase64 && (
                        <iframe
                            title="PDF Preview"
                            src={`data:application/pdf;base64,${pdfBase64}`}
                            width="100%"
                            height="100%"
                            style={{ border: "none" }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="secondary" onClick={() => setShowPdfModal(false)}>
                        Close
                    </Button>
                    <Button type="submit" className="btn btn-success" onClick={dskConfirmationFunc}>
                        Digi Sign
                    </Button>
                    <Button
                        type="button"
                        className="btn btn-primary sign-btn"
                        onClick={() => {
                            initiateESign({
                                stuids: selectedStuIds,
                            });
                        }
                        } >
                        E-Sign
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
